// Warehouse KPI page — the management analytics view.
//
// Complements DashboardPage (which shows *right now* state like slot
// occupancy + active trips) with *time-based* metrics derived from the
// WarehouseMovement audit trail: throughput over time, lead times between
// transitions, stock age, trip utilisation, and a stale-items watchlist.
//
// Everything comes from a single GET /api/v1/warehouse/kpis call via the
// useKpis hook, so the page is a pure render with no cross-hook
// coordination. Charts are hand-drawn SVG — sparkline + horizontal bars —
// to avoid adding a chart library dependency for two simple visualisations.

import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Alert,
  Table,
  Form,
  ProgressBar,
} from 'react-bootstrap';

import { useKpis } from '../../features/warehouse/useKpis.js';
import { typeBadgeColor } from '../../features/warehouse/warehouseUtils.js';

const WINDOW_OPTIONS = [7, 14, 30, 60, 90];

export default function KpiPage() {
  const [windowDays, setWindowDays] = useState(30);
  const { data, isLoading, error } = useKpis({ days: windowDays });

  if (isLoading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          Failed to load KPIs: {error.message}
        </Alert>
      </Container>
    );
  }

  if (!data) return null;

  const { headline, throughput, leadTimes, stockAge, tripUtilisation, staleItems } = data;

  return (
    <Container fluid className="py-3">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Warehouse KPIs</h4>
          <div className="text-muted small">
            Rolling {windowDays}-day window · generated{' '}
            {new Date(data.generatedAt).toLocaleString()}
          </div>
        </Col>
        <Col xs="auto">
          <Form.Label className="small mb-1">Window</Form.Label>
          <Form.Select
            size="sm"
            value={windowDays}
            onChange={e => setWindowDays(Number(e.target.value))}
            style={{ width: 150 }}
          >
            {WINDOW_OPTIONS.map(d => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {/* Headline row */}
      <Row className="g-3 mb-3">
        <Col md={3}>
          <StatCard
            label="Put-aways"
            primary={headline.putAwaysInWindow}
            secondary={`items received in ${windowDays}d`}
          />
        </Col>
        <Col md={3}>
          <StatCard
            label="Shipments"
            primary={headline.shipmentsInWindow}
            secondary={`items dispatched in ${windowDays}d`}
          />
        </Col>
        <Col md={3}>
          <StatCard
            label="Avg dock-to-stock"
            primary={
              headline.avgDockToStockH != null
                ? `${headline.avgDockToStockH.toFixed(1)} h`
                : '—'
            }
            secondary="produced → put-away"
          />
        </Col>
        <Col md={3}>
          <StatCard
            label="Avg dwell time"
            primary={
              headline.avgDwellDays != null
                ? `${headline.avgDwellDays.toFixed(1)} d`
                : '—'
            }
            secondary="put-away → loaded"
            variant={headline.avgDwellDays > 30 ? 'warning' : null}
          />
        </Col>
      </Row>

      <Row className="g-3">
        {/* Throughput sparkline */}
        <Col md={8}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Throughput — last {windowDays} days</strong>
              <div className="small">
                <span className="me-3">
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: '#198754',
                      marginRight: 4,
                    }}
                  />
                  Put-aways
                </span>
                <span className="me-3">
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: '#0d6efd',
                      marginRight: 4,
                    }}
                  />
                  Loaded
                </span>
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      background: '#dc3545',
                      marginRight: 4,
                    }}
                  />
                  Shipped
                </span>
              </div>
            </Card.Header>
            <Card.Body>
              <ThroughputChart data={throughput} />
            </Card.Body>
          </Card>
        </Col>

        {/* Trip utilisation */}
        <Col md={4}>
          <Card className="h-100">
            <Card.Header><strong>Trip utilisation</strong></Card.Header>
            <Card.Body>
              {tripUtilisation.sampleCount === 0 ? (
                <div className="text-muted small">
                  No trips shipped in this window.
                </div>
              ) : (
                <>
                  <div className="small text-muted mb-2">
                    Across {tripUtilisation.sampleCount} shipped{' '}
                    trip{tripUtilisation.sampleCount === 1 ? '' : 's'}
                  </div>
                  <div className="mb-2">
                    <div className="d-flex justify-content-between small">
                      <span className="text-muted">Weight used</span>
                      <strong>
                        {tripUtilisation.avgWeightKg.toLocaleString()} /{' '}
                        {tripUtilisation.truckCapacityKg.toLocaleString()} kg
                      </strong>
                    </div>
                    <ProgressBar
                      now={tripUtilisation.avgWeightPctOfCapacity}
                      variant={
                        tripUtilisation.avgWeightPctOfCapacity > 85
                          ? 'success'
                          : tripUtilisation.avgWeightPctOfCapacity > 60
                          ? 'info'
                          : 'warning'
                      }
                      label={`${tripUtilisation.avgWeightPctOfCapacity}%`}
                      style={{ height: 18 }}
                    />
                  </div>
                  <Table size="sm" className="mb-0">
                    <tbody>
                      <tr>
                        <td className="text-muted">Avg length/trip</td>
                        <td className="text-end">
                          <strong>{tripUtilisation.avgLengthM} m</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Avg items/trip</td>
                        <td className="text-end">
                          <strong>{tripUtilisation.avgItemsPerTrip}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Truck bed</td>
                        <td className="text-end small">
                          {tripUtilisation.truckBedLengthM} m (tautliner)
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-0">
        {/* Lead time table */}
        <Col md={5}>
          <Card className="h-100">
            <Card.Header><strong>Lead times</strong></Card.Header>
            <Card.Body className="p-0">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Transition</th>
                    <th className="text-end">Samples</th>
                    <th className="text-end">Avg</th>
                    <th className="text-end">P50</th>
                    <th className="text-end">P90</th>
                  </tr>
                </thead>
                <tbody>
                  <LeadTimeRow
                    label="Produced → put-away"
                    sub="Dock-to-stock"
                    stat={leadTimes.dockToStock}
                  />
                  <LeadTimeRow
                    label="Put-away → loaded"
                    sub="Storage dwell"
                    stat={leadTimes.dwell}
                  />
                  <LeadTimeRow
                    label="Loaded → shipped"
                    sub="Load-to-depart"
                    stat={leadTimes.loadToDepart}
                  />
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Stock age histogram */}
        <Col md={7}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Stock age</strong>
              <span className="small text-muted">
                {stockAge.totalItems} items currently in slots
              </span>
            </Card.Header>
            <Card.Body>
              <StockAgeHistogram buckets={stockAge.buckets} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Stale items watchlist */}
      <Row className="mt-3">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Stale items watchlist</strong>
              <Badge bg={headline.staleItemCount > 0 ? 'warning' : 'secondary'}>
                {headline.staleItemCount} items &gt; 30 days
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {staleItems.length === 0 ? (
                <div className="text-muted small text-center py-4">
                  No stale items — every item in stock is younger than 30 days.
                </div>
              ) : (
                <Table size="sm" hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Serial</th>
                      <th>Type</th>
                      <th>Cable</th>
                      <th>Slot</th>
                      <th className="text-end">Length</th>
                      <th className="text-end">Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staleItems.map(item => (
                      <tr key={item.serial}>
                        <td><strong>{item.serial}</strong></td>
                        <td>
                          <Badge bg={typeBadgeColor(item.type)}>{item.type}</Badge>
                        </td>
                        <td className="small">{item.cableName}</td>
                        <td className="small">
                          {item.slotCode || <span className="text-muted">—</span>}
                        </td>
                        <td className="text-end">{item.lengthMeters} m</td>
                        <td className="text-end">
                          <Badge bg={item.ageDays > 90 ? 'danger' : item.ageDays > 60 ? 'warning' : 'secondary'}>
                            {item.ageDays} d
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

// ---------------------------------------------------------------------------

function StatCard({ label, primary, secondary, variant }) {
  const borderClass =
    variant === 'warning' ? 'border-warning' : variant === 'danger' ? 'border-danger' : '';
  return (
    <Card className={`h-100 ${borderClass}`}>
      <Card.Body>
        <div className="text-muted small text-uppercase">{label}</div>
        <div className="h3 mb-0 mt-1">{primary}</div>
        {secondary && <div className="small text-muted">{secondary}</div>}
      </Card.Body>
    </Card>
  );
}

function LeadTimeRow({ label, sub, stat }) {
  const isEmpty = !stat || stat.samples === 0;
  const unitLabel = stat?.unit === 'days' ? 'd' : 'h';
  const fmt = (v) =>
    v == null ? '—' : `${Number(v).toFixed(1)} ${unitLabel}`;

  return (
    <tr>
      <td>
        <div className="small">
          <strong>{label}</strong>
        </div>
        <div className="text-muted" style={{ fontSize: '0.7rem' }}>{sub}</div>
      </td>
      <td className="text-end">{stat?.samples || 0}</td>
      <td className="text-end">{isEmpty ? '—' : fmt(stat.avg)}</td>
      <td className="text-end">{isEmpty ? '—' : fmt(stat.p50)}</td>
      <td className="text-end">{isEmpty ? '—' : fmt(stat.p90)}</td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Hand-drawn SVG throughput chart — three polylines (put-aways, loaded,
// shipped) over the same X axis. No chart library because we'd be pulling
// ~120 KB for what amounts to nine line segments.
// ---------------------------------------------------------------------------

function ThroughputChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="text-muted small">No data.</div>;
  }

  const W = 700;
  const H = 220;
  const padL = 32;
  const padR = 10;
  const padT = 10;
  const padB = 30;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const maxY = Math.max(
    3,
    ...data.map(d => Math.max(d.putAway, d.loaded, d.shipped))
  );

  const xFor = (i) =>
    padL + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const yFor = (v) => padT + innerH - (v / maxY) * innerH;

  const linePts = (key) =>
    data.map((d, i) => `${xFor(i)},${yFor(d[key])}`).join(' ');

  // Y axis ticks: 0, mid, max
  const ticks = [0, Math.round(maxY / 2), maxY];

  // X axis labels: first, middle, last
  const xLabels = [
    { i: 0, label: data[0].date.slice(5) },
    { i: Math.floor(data.length / 2), label: data[Math.floor(data.length / 2)].date.slice(5) },
    { i: data.length - 1, label: data[data.length - 1].date.slice(5) },
  ];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
      {/* Y axis gridlines + tick labels */}
      {ticks.map(t => (
        <g key={`tick-${t}`}>
          <line
            x1={padL}
            y1={yFor(t)}
            x2={W - padR}
            y2={yFor(t)}
            stroke="#e9ecef"
            strokeWidth="1"
          />
          <text
            x={padL - 6}
            y={yFor(t) + 4}
            fontSize="10"
            fill="#6c757d"
            textAnchor="end"
          >
            {t}
          </text>
        </g>
      ))}

      {/* X axis labels */}
      {xLabels.map(({ i, label }) => (
        <text
          key={`xlabel-${i}`}
          x={xFor(i)}
          y={H - padB + 16}
          fontSize="10"
          fill="#6c757d"
          textAnchor="middle"
        >
          {label}
        </text>
      ))}

      {/* Three data lines */}
      <polyline
        points={linePts('putAway')}
        fill="none"
        stroke="#198754"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points={linePts('loaded')}
        fill="none"
        stroke="#0d6efd"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polyline
        points={linePts('shipped')}
        fill="none"
        stroke="#dc3545"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Data point dots (every other day if > 20 days to keep it readable) */}
      {data.map((d, i) => {
        const stride = data.length > 30 ? 3 : data.length > 14 ? 2 : 1;
        if (i % stride !== 0 && i !== data.length - 1) return null;
        return (
          <g key={`pt-${i}`}>
            {d.putAway > 0 && (
              <circle cx={xFor(i)} cy={yFor(d.putAway)} r="2.5" fill="#198754" />
            )}
            {d.loaded > 0 && (
              <circle cx={xFor(i)} cy={yFor(d.loaded)} r="2.5" fill="#0d6efd" />
            )}
            {d.shipped > 0 && (
              <circle cx={xFor(i)} cy={yFor(d.shipped)} r="2.5" fill="#dc3545" />
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Stock age histogram — horizontal bars, one per age bucket.
// ---------------------------------------------------------------------------

function StockAgeHistogram({ buckets }) {
  const total = buckets.reduce((a, b) => a + b.count, 0);
  if (total === 0) {
    return <div className="text-muted small">No stock in slots.</div>;
  }
  const maxCount = Math.max(...buckets.map(b => b.count));

  // Red ramp — fresher buckets blue, older ones red, to draw the eye to
  // the right-hand tail. Matches the age-badge coloring in the stale list.
  const colorFor = (label) => {
    if (label === '0-7d') return '#0d6efd';
    if (label === '7-30d') return '#198754';
    if (label === '30-60d') return '#ffc107';
    if (label === '60-90d') return '#fd7e14';
    return '#dc3545';
  };

  return (
    <div>
      {buckets.map(b => {
        const pct = maxCount ? (b.count / maxCount) * 100 : 0;
        return (
          <div key={b.label} className="mb-2">
            <div className="d-flex justify-content-between small">
              <strong>{b.label}</strong>
              <span className="text-muted">
                {b.count} items · {(b.lengthM / 1000).toFixed(1)} km
              </span>
            </div>
            <div
              style={{
                height: 18,
                background: '#e9ecef',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${pct}%`,
                  height: '100%',
                  background: colorFor(b.label),
                  transition: 'width 0.25s ease',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
